import { useState, useEffect } from 'react'
import AdminHeader from '../../components/admin/AdminHeader'
import AdminSidebar from '../../components/admin/AdminSidebar'
import { processFile, formatFileSize, checkFileSize } from '../../utils/fileCompression'
import { API_BASE_URL, MAX_IMAGES } from '../../config/api'

interface Event {
  id: number
  title: string
  subtitle?: string
  date: string
  start_date?: string
  end_date?: string
  googledrivelink: string
  location: string
  image_path?: string
  image?: string
  thumbnail_path?: string
  thumbnail?: string // Can be a URL string from API
  media?: Array<{
    id?: number
    path?: string
    url?: string
    type?: 'image' | 'video'
    media_type?: 'image' | 'video' // API response format
    media_url?: string // API response format
  }>
  description: string
  status: 'upcoming' | 'past'
  ticket_url?: string
  is_active?: number
  created_at?: string
  updated_at?: string
}

const EventManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewingEvent, setViewingEvent] = useState<Event | null>(null)
  const [editingEvent, setEditingEvent] = useState<Event | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    date: '',
    start_date: '',
    end_date: '',
    googledrivelink: '',
    location: '',
    description: '',
    ticket_url: '',
    is_active: 1,
    thumbnail: null as File | null,
    mediaFiles: [] as File[]
  })

  // Calculate status based on end_date
  const calculateStatus = (endDate: string): 'upcoming' | 'past' => {
    if (!endDate) return 'upcoming'
    const end = new Date(endDate)
    const now = new Date()
    return end >= now ? 'upcoming' : 'past'
  }

  // Format date with time (e.g., "22 March 2026 12:24pm")
  const formatDateWithTime = (dateTimeString: string): string => {
    if (!dateTimeString) return ''
    const date = new Date(dateTimeString)
    
    // Get day, month, year
    const day = date.getDate()
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December']
    const month = monthNames[date.getMonth()]
    const year = date.getFullYear()
    
    // Get time in 12-hour format
    let hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'pm' : 'am'
    hours = hours % 12
    hours = hours ? hours : 12 // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes
    
    return `${day} ${month} ${year} ${hours}:${minutesStr}${ampm}`
  }

  // Format display date with both start and end dates: "22 March 2026 12:24pm to 22 March 2026 3:30pm"
  const formatDisplayDateRange = (startDate: string, endDate: string): string => {
    if (!startDate) return ''
    if (!endDate) {
      // If no end date, just return start date with time
      return formatDateWithTime(startDate)
    }
    
    const startFormatted = formatDateWithTime(startDate)
    const endFormatted = formatDateWithTime(endDate)
    
    return `${startFormatted} to ${endFormatted}`
  }

  // Parse display date to extract start date (for editing)
  // Handles both formats: "22 March 2026 12:24pm to 22 March 2026 3:30pm" or "22 March 2026 12:24pm"
  const parseDisplayDateToStartDate = (displayDate: string): string => {
    if (!displayDate) return ''
    
    // Check if it contains " to " (range format)
    if (displayDate.includes(' to ')) {
      // Extract the start date part (before " to ")
      const startPart = displayDate.split(' to ')[0].trim()
      return parseDateStringToDatetimeLocal(startPart)
    } else {
      // Single date format
      return parseDateStringToDatetimeLocal(displayDate)
    }
  }

  // Parse display date to extract end date (for editing)
  // Handles format: "22 March 2026 12:24pm to 22 March 2026 3:30pm"
  const parseDisplayDateToEndDate = (displayDate: string): string => {
    if (!displayDate) return ''
    
    // Check if it contains " to " (range format)
    if (displayDate.includes(' to ')) {
      // Extract the end date part (after " to ")
      const endPart = displayDate.split(' to ')[1].trim()
      return parseDateStringToDatetimeLocal(endPart)
    }
    
    // No end date in display format
    return ''
  }

  // Convert date string to datetime-local format (handles various input formats)
  const convertToDatetimeLocal = (dateString: string): string => {
    if (!dateString) return ''
    
    // If already in datetime-local format (YYYY-MM-DDTHH:mm), return as is
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateString)) {
      return dateString
    }
    
    // Try to parse as Date object
    try {
      const date = new Date(dateString)
      if (!isNaN(date.getTime())) {
        // Format to datetime-local (YYYY-MM-DDTHH:mm)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        
        return `${year}-${month}-${day}T${hours}:${minutes}`
      }
    } catch (err) {
      console.error('Error converting date to datetime-local:', err)
    }
    
    // If parsing fails, try the parseDateStringToDatetimeLocal function
    return parseDateStringToDatetimeLocal(dateString)
  }

  // Parse date string like "22 March 2026 12:24pm" to datetime-local format
  const parseDateStringToDatetimeLocal = (dateString: string): string => {
    if (!dateString) return ''
    
    try {
      // Try to parse the date string
      const date = new Date(dateString)
      if (isNaN(date.getTime())) {
        // If direct parsing fails, try manual parsing
        // Format: "22 March 2026 12:24pm"
        const match = dateString.match(/(\d+)\s+(\w+)\s+(\d+)\s+(\d+):(\d+)(am|pm)/i)
        if (match) {
          const day = parseInt(match[1])
          const monthName = match[2]
          const year = parseInt(match[3])
          let hours = parseInt(match[4])
          const minutes = parseInt(match[5])
          const ampm = match[6].toLowerCase()
          
          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                            'July', 'August', 'September', 'October', 'November', 'December']
          const month = monthNames.findIndex(m => m.toLowerCase() === monthName.toLowerCase())
          
          if (ampm === 'pm' && hours !== 12) hours += 12
          if (ampm === 'am' && hours === 12) hours = 0
          
          const dateObj = new Date(year, month, day, hours, minutes)
          
          // Format to datetime-local (YYYY-MM-DDTHH:mm)
          const yearStr = dateObj.getFullYear()
          const monthStr = String(dateObj.getMonth() + 1).padStart(2, '0')
          const dayStr = String(dateObj.getDate()).padStart(2, '0')
          const hoursStr = String(dateObj.getHours()).padStart(2, '0')
          const minutesStr = String(dateObj.getMinutes()).padStart(2, '0')
          
          return `${yearStr}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}`
        }
      } else {
        // Format to datetime-local
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        
        return `${year}-${month}-${day}T${hours}:${minutes}`
      }
    } catch (err) {
      console.error('Error parsing date:', err)
    }
    
    return ''
  }

  // Extract start date from stored display date for UI display
  const extractStartDateForDisplay = (displayDate: string): string => {
    if (!displayDate) return ''
    
    // If it contains " to ", extract the start part
    if (displayDate.includes(' to ')) {
      return displayDate.split(' to ')[0].trim()
    }
    
    // Otherwise return as is
    return displayDate
  }

  // Helper function to get full image URL from image_path
  const getImageUrl = (event: Event): string | null => {
    if (event.image_path) {
      const cleanPath = event.image_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    return event.image || null
  }

  // Helper function to get thumbnail URL
  const getThumbnailUrl = (event: Event): string | null => {
    if (event.thumbnail_path) {
      const cleanPath = event.thumbnail_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    return event.thumbnail || null
  }

  // Helper function to get media URLs
  const getMediaUrls = (event: Event): Array<{ url: string; type: 'image' | 'video' }> => {
    if (!event.media || event.media.length === 0) return []
    return event.media.map(media => {
      let url = ''
      // Check API response format first (media_url)
      if (media.media_url) {
        url = media.media_url
      } else if (media.url) {
        url = media.url
      } else if (media.path) {
        const cleanPath = media.path.replace(/\\/g, '/')
        url = `${API_BASE_URL}${cleanPath}`
      }
      
      // Determine type from API response format or infer from URL
      let type: 'image' | 'video' = 'image'
      if (media.media_type) {
        type = media.media_type as 'image' | 'video'
      } else if (media.type) {
        type = media.type
      } else if (url.match(/\.(mp4|webm|ogg|mov)$/i)) {
        type = 'video'
      }
      
      return { url, type }
    }).filter(m => m.url)
  }

  // Fetch events
  const fetchEvents = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}event_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Recalculate status for events based on end_date
        const eventsWithCalculatedStatus = result.data.map((event: Event) => {
          if (event.end_date) {
            return { ...event, status: calculateStatus(event.end_date) }
          }
          return event
        })
        setEvents(eventsWithCalculatedStatus)
      } else {
        setError(result.message || 'Failed to fetch events')
      }
    } catch (err) {
      setError('Failed to fetch events. Please try again.')
      console.error('Error fetching events:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  // Handle thumbnail file selection
  const handleThumbnailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return

    const file = e.target.files[0]
    setError('')
    setUploadProgress('')

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      setError('Thumbnail must be an image file')
      e.target.value = ''
      return
    }

    // Check file size before processing
    const sizeCheck = checkFileSize(file)
    if (!sizeCheck.valid) {
      setError(sizeCheck.message || 'File is too large')
      e.target.value = ''
      return
    }

    setUploadProgress(`Processing thumbnail (${formatFileSize(file.size)})...`)

    // Process and compress file
    const result = await processFile(file)
    
    if (result.error) {
      setError(result.error)
      e.target.value = ''
      return
    }

    setFormData({ ...formData, thumbnail: result.file })
    setUploadProgress(`Thumbnail ready: ${formatFileSize(result.file.size)}`)
  }
// Handle multiple media files selection
const handleMediaFilesChange = async (
  e: React.ChangeEvent<HTMLInputElement>
) => {
  if (!e.target.files || e.target.files.length === 0) return

  const files = Array.from(e.target.files)
  setError('')
  setUploadProgress('')

  // 🔒 Check max media files limit
  const existingCount = formData.mediaFiles.length
  const allowedCount = MAX_IMAGES - existingCount

  if (allowedCount <= 0) {
    setError(`You can upload a maximum of ${MAX_IMAGES} media files.`)
    e.target.value = ''
    return
  }

  if (files.length > allowedCount) {
    setError(
      `Only ${allowedCount} more file(s) allowed. Max ${MAX_IMAGES} files.`
    )
  }

  // Process only allowed number of files
  const filesToProcess = files.slice(0, allowedCount)

  const processedFiles: File[] = []
  let hasError = false

  for (let i = 0; i < filesToProcess.length; i++) {
    const file = filesToProcess[i]

    // Check file size
    const sizeCheck = checkFileSize(file)
    if (!sizeCheck.valid) {
      setError(`${file.name}: ${sizeCheck.message || 'File is too large'}`)
      hasError = true
      continue
    }

    setUploadProgress(
      `Processing ${i + 1}/${filesToProcess.length}: ${file.name}...`
    )

    // Process file (compress if image)
    const result = await processFile(file)

    if (result.error) {
      setError(`${file.name}: ${result.error}`)
      hasError = true
      continue
    }

    processedFiles.push(result.file)
  }

  if (hasError && processedFiles.length === 0) {
    e.target.value = ''
    return
  }

  // ✅ Add processed files
  setFormData({
    ...formData,
    mediaFiles: [...formData.mediaFiles, ...processedFiles]
  })

  setUploadProgress(`Added ${processedFiles.length} file(s)`)
  e.target.value = '' // Reset input
}
  // Remove a media file from the list
  const removeMediaFile = (index: number) => {
    const newMediaFiles = formData.mediaFiles.filter((_, i) => i !== index)
    setFormData({ ...formData, mediaFiles: newMediaFiles })
  }

  // Handle create
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    
    if (!formData.thumbnail && formData.mediaFiles.length === 0) {
      setError('Please select at least a thumbnail or media files')
      return
    }

    setUploading(true)
    setUploadProgress('Uploading...')

    try {
      // Calculate status based on end_date
      const calculatedStatus = calculateStatus(formData.end_date)
      
      const formDataToSend = new FormData()
      formDataToSend.append('title', formData.title)
      formDataToSend.append('subtitle', formData.subtitle || '')
      formDataToSend.append('date', formData.date)
      formDataToSend.append('googledrivelink', formData.googledrivelink)
      formDataToSend.append('location', formData.location)
      formDataToSend.append('description', formData.description)
      formDataToSend.append('status', calculatedStatus) // Auto-calculated from end_date
      formDataToSend.append('ticket_url', formData.ticket_url || '')
      formDataToSend.append('is_active', formData.is_active.toString())

      // Add thumbnail if provided
      if (formData.thumbnail) {
        formDataToSend.append('thumbnail', formData.thumbnail)
      }

      // Add media files
      formData.mediaFiles.forEach((file) => {
        formDataToSend.append('media[]', file)
      })

      const response = await fetch(`${API_BASE_URL}event_create.php`, {
        method: 'POST',
        body: formDataToSend
      })

      const result = await response.json()

      if (result.status) {
        const eventId = result?.event_id
        
        // Automatically create event gallery if there are image files
        const imageFiles = formData.mediaFiles.filter(file => file.type.startsWith('image/'))
        
        if (eventId && imageFiles.length > 0) {
          setUploadProgress('Creating event gallery...')
          
          try {
            const galleryFormData = new FormData()
            galleryFormData.append('event_id', eventId.toString())
            
            // Add all image files to gallery
            imageFiles.forEach((file) => {
              galleryFormData.append('gallery_images[]', file)
            })
            
            // Set first image as thumbnail (index 0)
            galleryFormData.append('thumbnail_index', '0')
            
            // Add default titles for images
            imageFiles.forEach((file, index) => {
              console.log(file)
              galleryFormData.append('image_titles[]', `Gallery Image ${index + 1}`)
            })
            
            const galleryResponse = await fetch(`${API_BASE_URL}event_gallery_create.php`, {
              method: 'POST',
              body: galleryFormData
            })
            
            const galleryResult = await galleryResponse.json()
            
            if (galleryResult.status) {
              setSuccess('Event and gallery created successfully!')
            } else {
              setSuccess('Event created successfully, but gallery creation failed: ' + (galleryResult.message || 'Unknown error'))
              console.warn('Gallery creation failed:', galleryResult)
            }
          } catch (galleryErr) {
            setSuccess('Event created successfully, but gallery creation failed: ' + (galleryErr instanceof Error ? galleryErr.message : 'Unknown error'))
            console.error('Error creating gallery:', galleryErr)
          }
        } else {
          setSuccess('Event created successfully!')
        }
        
        resetForm()
        setShowAddModal(false)
        setUploadProgress('')
        fetchEvents()
        
        setTimeout(() => setSuccess(''), 5000)
      } else {
        setError(result.message || 'Failed to create event')
      }
    } catch (err) {
      setError('Failed to create event. Please try again.')
      console.error('Error creating event:', err)
    } finally {
      setUploading(false)
      setUploadProgress('')
    }
  }

  // Handle update
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingEvent) return

    setError('')
    setSuccess('')
    setUploading(true)

    try {
      // Calculate status based on end_date
      const calculatedStatus = calculateStatus(formData.end_date)
      
      const formDataToSend = new FormData()
      formDataToSend.append('id', editingEvent.id.toString())
      formDataToSend.append('title', formData.title)
      formDataToSend.append('subtitle', formData.subtitle || '')
      formDataToSend.append('date', formData.date)
      formDataToSend.append('googledrivelink', formData.googledrivelink)
      formDataToSend.append('location', formData.location)
      formDataToSend.append('description', formData.description)
      formDataToSend.append('status', calculatedStatus) // Auto-calculated from end_date
      formDataToSend.append('ticket_url', formData.ticket_url || '')
      formDataToSend.append('is_active', formData.is_active.toString())
      
      // Add thumbnail if a new one is selected
      if (formData.thumbnail) {
        const sizeCheck = checkFileSize(formData.thumbnail)
        if (!sizeCheck.valid) {
          setError(sizeCheck.message || 'Thumbnail file is too large')
          setUploading(false)
          return
        }
        formDataToSend.append('thumbnail', formData.thumbnail)
      }

      // Add media files if any are selected
      formData.mediaFiles.forEach((file) => {
        const sizeCheck = checkFileSize(file)
        if (!sizeCheck.valid) {
          setError(`${file.name}: ${sizeCheck.message || 'File is too large'}`)
          setUploading(false)
          return
        }
        formDataToSend.append('media[]', file)
      })

      const response = await fetch(`${API_BASE_URL}event_update.php`, {
        method: 'POST',
        body: formDataToSend
      })

      const result = await response.json()

      if (result.status) {
        const eventId = editingEvent.id
        
        // Automatically add new image files to event gallery if there are any
        const imageFiles = formData.mediaFiles.filter(file => file.type.startsWith('image/'))
        
        if (eventId && imageFiles.length > 0) {
          setUploadProgress('Adding images to event gallery...')
          
          try {
            // First, check existing gallery count
            const galleryCheckResponse = await fetch(`${API_BASE_URL}event_gallery_list.php?event_id=${eventId}`)
            const galleryCheckResult = await galleryCheckResponse.json()
            
            let existingImageCount = 0
            if (galleryCheckResult.status && galleryCheckResult.data) {
              existingImageCount = galleryCheckResult.data.length
            }
            
            const totalCount = existingImageCount + imageFiles.length
            
            if (totalCount > MAX_IMAGES) {
              setSuccess(`Event updated successfully! However, you tried to add ${imageFiles.length} images, but the gallery already has ${existingImageCount} images. Maximum is ${MAX_IMAGES} images. Only ${MAX_IMAGES - existingImageCount} image(s) will be added.`)
              
              // Only add images up to the limit
              const imagesToAdd = imageFiles.slice(0, MAX_IMAGES - existingImageCount)
              
              if (imagesToAdd.length > 0) {
                const galleryFormData = new FormData()
                galleryFormData.append('event_id', eventId.toString())
                
                imagesToAdd.forEach((file) => {
                  galleryFormData.append('gallery_images[]', file)
                })
                
                // Add default titles for images
                imagesToAdd.forEach((file, index) => {
                  console.log(file)
                  galleryFormData.append('image_titles[]', `Gallery Image ${existingImageCount + index + 1}`)
                })
                
                const galleryResponse = await fetch(`${API_BASE_URL}event_gallery_create.php`, {
                  method: 'POST',
                  body: galleryFormData
                })
                
                const galleryResult = await galleryResponse.json()
                
                if (!galleryResult.status) {
                  console.warn('Gallery update failed:', galleryResult)
                }
              }
            } else {
              // Add all images
              const galleryFormData = new FormData()
              galleryFormData.append('event_id', eventId.toString())
              
              imageFiles.forEach((file) => {
                galleryFormData.append('gallery_images[]', file)
              })
              
              // Add default titles for images
              imageFiles.forEach((file, index) => {
                console.log(file)
                galleryFormData.append('image_titles[]', `Gallery Image ${existingImageCount + index + 1}`)
              })
              
              const galleryResponse = await fetch(`${API_BASE_URL}event_gallery_create.php`, {
                method: 'POST',
                body: galleryFormData
              })
              
              const galleryResult = await galleryResponse.json()
              
              if (galleryResult.status) {
                setSuccess(`Event updated successfully! Added ${imageFiles.length} image(s) to gallery.`)
              } else {
                setSuccess('Event updated successfully, but gallery update failed: ' + (galleryResult.message || 'Unknown error'))
                console.warn('Gallery update failed:', galleryResult)
              }
            }
          } catch (galleryErr) {
            setSuccess('Event updated successfully, but gallery update failed: ' + (galleryErr instanceof Error ? galleryErr.message : 'Unknown error'))
            console.error('Error updating gallery:', galleryErr)
          }
        } else {
          setSuccess('Event updated successfully!')
        }
        
        setShowEditModal(false)
        setEditingEvent(null)
        resetForm()
        fetchEvents()
        
        setTimeout(() => setSuccess(''), 5000)
      } else {
        setError(result.message || 'Failed to update event')
      }
    } catch (err) {
      setError('Failed to update event. Please try again.')
      console.error('Error updating event:', err)
    } finally {
      setUploading(false)
    }
  }

  // Handle delete
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return
    }

    setError('')
    setSuccess('')

    try {
      const response = await fetch(`${API_BASE_URL}event_delete.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Event deleted successfully!')
        fetchEvents()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to delete event')
      }
    } catch (err) {
      setError('Failed to delete event. Please try again.')
      console.error('Error deleting event:', err)
    }
  }

  // Open edit modal
  const openEditModal = (event: Event) => {
    setEditingEvent(event)
    
    // Parse the stored display date to extract start_date and end_date
    let startDate = event.start_date || ''
    let endDate = event.end_date || ''
    let displayDate = event.date || ''
    
    // If display date exists but start_date doesn't, try to parse it
    if (displayDate && !startDate) {
      startDate = parseDisplayDateToStartDate(displayDate)
    }
    
    // If display date exists but end_date doesn't, try to parse it from display date
    if (displayDate && !endDate && displayDate.includes(' to ')) {
      endDate = parseDisplayDateToEndDate(displayDate)
    }
    
    // Convert dates to datetime-local format for input fields
    startDate = startDate ? convertToDatetimeLocal(startDate) : ''
    endDate = endDate ? convertToDatetimeLocal(endDate) : ''
    
    // If we have both start_date and end_date but display_date is in old format, update it
    if (startDate && endDate && displayDate && !displayDate.includes(' to ')) {
      displayDate = formatDisplayDateRange(startDate, endDate)
    }
    
    // If we have dates but no display date, create it
    if (startDate && endDate && !displayDate) {
      displayDate = formatDisplayDateRange(startDate, endDate)
    } else if (startDate && !displayDate) {
      displayDate = formatDateWithTime(startDate)
    }
    
    setFormData({
      title: event.title,
      subtitle: event.subtitle || '',
      date: displayDate,
      start_date: startDate,
      end_date: endDate,
      googledrivelink: event.googledrivelink,
      location: event.location,
      description: event.description,
      ticket_url: event.ticket_url || '',
      is_active: event.is_active || 1,
      thumbnail: null,
      mediaFiles: []
    })
    setUploadProgress('')
    setError('')
    setShowEditModal(true)
  }

  // Open view modal
  const openViewModal = (event: Event) => {
    setViewingEvent(event)
    setShowViewModal(true)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      date: '',
      start_date: '',
      end_date: '',
      googledrivelink: '',
      location: '',
      description: '',
      ticket_url: '',
      is_active: 1,
      thumbnail: null,
      mediaFiles: []
    })
    setError('')
    setUploadProgress('')
  }

  // Filter events - calculate status for events that don't have it
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('all')
  const filteredEvents = filter === 'all' 
    ? events 
    : filter === 'upcoming' 
    ? events.filter(e => {
        // If end_date exists, calculate status, otherwise use existing status
        if (e.end_date) {
          return calculateStatus(e.end_date) === 'upcoming'
        }
        return e.status === 'upcoming'
      })
    : events.filter(e => {
        // If end_date exists, calculate status, otherwise use existing status
        if (e.end_date) {
          return calculateStatus(e.end_date) === 'past'
        }
        return e.status === 'past'
      })

  return (
    <div className="min-h-screen bg-gray-900">
      <AdminHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex">
        <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 md:ml-64 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-white">Event Management</h1>
              <button
                onClick={() => {
                  resetForm()
                  setShowAddModal(true)
                }}
                className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add New Event</span>
              </button>
            </div>

            {/* Success/Error Messages */}
            {success && (
              <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg animate-fadeIn">
                <p className="text-green-400">{success}</p>
              </div>
            )}
            {error && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg animate-fadeIn">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-4 mb-6">
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  filter === 'all'
                    ? 'bg-primary-light text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                All Events ({events.length})
              </button>
              <button
                onClick={() => setFilter('upcoming')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  filter === 'upcoming'
                    ? 'bg-primary-light text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Upcoming ({events.filter(e => e.status === 'upcoming').length})
              </button>
              <button
                onClick={() => setFilter('past')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  filter === 'past'
                    ? 'bg-primary-light text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Past ({events.filter(e => e.status === 'past').length})
              </button>
            </div>

            {/* Events Grid */}
            {loading ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light mx-auto mb-4"></div>
                <p className="text-gray-400 text-lg">Loading events...</p>
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-400 text-lg">No events found. Add your first event!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredEvents.map((event) => {
                  const thumbnailUrl = getThumbnailUrl(event) || getImageUrl(event)
                  return (
                    <div key={event.id} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                      <div className="relative h-48 bg-gray-700">
                        {thumbnailUrl ? (
                          <img src={thumbnailUrl} alt={event.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-16 h-16 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                        <div className="absolute top-2 right-2 flex flex-col gap-2">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              event.status === 'upcoming'
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-600 text-gray-300'
                            }`}
                          >
                            {event.status === 'upcoming' ? 'Upcoming' : 'Past'}
                          </span>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              (event.is_active ?? 1) === 1
                                ? 'bg-blue-500 text-white'
                                : 'bg-red-500 text-white'
                            }`}
                          >
                            {(event.is_active ?? 1) === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="absolute top-2 left-2 bg-primary-light text-white px-3 py-1 rounded-lg font-semibold text-sm">
                          {extractStartDateForDisplay(event.date)}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-white font-bold text-lg mb-1 line-clamp-2">{event.title}</h3>
                        {event.subtitle && (
                          <p className="text-primary-light text-sm font-semibold mb-2">{event.subtitle}</p>
                        )}
                        <div className="flex items-center text-gray-400 text-xs mb-3">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="line-clamp-1">{event.location}</span>
                        </div>
                        <p className="text-gray-300 text-sm mb-4 line-clamp-2">{event.description}</p>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openViewModal(event)}
                            className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() => openEditModal(event)}
                            className="flex-1 px-3 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(event.id)}
                            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add Event Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-2xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Add New Event</h2>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="title" className="block text-white font-semibold mb-2">
                    Event Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    placeholder="Enter event title"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="subtitle" className="block text-white font-semibold mb-2">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    id="subtitle"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    placeholder="Enter subtitle (optional)"
                  />
                </div>

                <div>
                  <label htmlFor="start_date" className="block text-white font-semibold mb-2">
                    Event Start Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    id="start_date"
                    value={formData.start_date}
                    onChange={(e) => {
                      const selectedDate = e.target.value
                      // Auto-update display date with both start and end dates
                      const displayDate = formData.end_date 
                        ? formatDisplayDateRange(selectedDate, formData.end_date)
                        : formatDateWithTime(selectedDate)
                      setFormData({ 
                        ...formData, 
                        start_date: selectedDate,
                        date: displayDate
                      })
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="end_date" className="block text-white font-semibold mb-2">
                    Event End Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    id="end_date"
                    value={formData.end_date}
                    onChange={(e) => {
                      const selectedEndDate = e.target.value
                      // Auto-update display date with both start and end dates
                      const displayDate = formData.start_date 
                        ? formatDisplayDateRange(formData.start_date, selectedEndDate)
                        : formatDateWithTime(selectedEndDate)
                      setFormData({ 
                        ...formData, 
                        end_date: selectedEndDate,
                        date: displayDate
                      })
                      // Auto-calculate and show status preview
                      const calculatedStatus = calculateStatus(selectedEndDate)
                      if (calculatedStatus === 'past') {
                        setError('')
                        setSuccess('Status will be set to: Past (end date is in the past)')
                      } else {
                        setError('')
                        setSuccess('Status will be set to: Upcoming (end date is in the future)')
                      }
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  />
                  {formData.end_date && (
                    <p className="mt-2 text-sm text-primary-light">
                      Status: {calculateStatus(formData.end_date) === 'upcoming' ? 'Upcoming' : 'Past'} (auto-calculated)
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="date" className="block text-white font-semibold mb-2">
                    Display Date * (Auto-filled: Start Date to End Date with times)
                  </label>
                  <input
                    type="text"
                    id="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    placeholder="e.g., 22 March 2026 12:24pm to 22 March 2026 3:30pm"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Format: "DD MMMM YYYY HH:MMam/pm to DD MMMM YYYY HH:MMam/pm". Auto-filled from Start & End dates, but you can edit manually.
                  </p>
                  {formData.date && (
                    <p className="mt-2 text-sm text-gray-300">
                      <span className="font-semibold">Preview (Start Date Only):</span> {extractStartDateForDisplay(formData.date)}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="googledrivelink" className="block text-white font-semibold mb-2">
                    Google Drive Link
                  </label>
                  <input
                    type="text"
                    id="googledrivelink"
                    value={formData.googledrivelink}
                    onChange={(e) => setFormData({ ...formData, googledrivelink: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    placeholder="Enter event googledrivelink"
                    
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="location" className="block text-white font-semibold mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    placeholder="Enter event location"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="description" className="block text-white font-semibold mb-2">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light resize-none"
                    placeholder="Enter event description"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="is_active" className="block text-white font-semibold mb-2">
                    Active Status *
                  </label>
                  <select
                    id="is_active"
                    value={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="ticket_url" className="block text-white font-semibold mb-2">
                    Ticket URL
                  </label>
                  <input
                    type="url"
                    id="ticket_url"
                    value={formData.ticket_url}
                    onChange={(e) => setFormData({ ...formData, ticket_url: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    placeholder="https://www.ticketmaster.com/event/..."
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="thumbnail" className="block text-white font-semibold mb-2">
                    Thumbnail Image (Optional, Max 50MB)
                  </label>
                  <input
                    type="file"
                    id="thumbnail"
                    accept="image/*"
                    onChange={handleThumbnailChange}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  />
                  {uploadProgress && uploadProgress.includes('thumbnail') && (
                    <p className="mt-2 text-sm text-gray-400">{uploadProgress}</p>
                  )}
                  {formData.thumbnail && (
                    <div className="mt-2 flex items-center justify-between p-2 bg-gray-700 rounded">
                      <p className="text-sm text-green-400">
                        {formData.thumbnail.name} ({formatFileSize(formData.thumbnail.size)})
                      </p>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, thumbnail: null })}
                        className="text-red-400 hover:text-red-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="mediaFiles" className="block text-white font-semibold mb-2">
                    Media Files (Images & Videos) - Multiple files allowed (Max 50MB each)
                  </label>
                  <input
                    type="file"
                    id="mediaFiles"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleMediaFilesChange}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    You can select multiple images and videos at once
                  </p>
                  <p className="mt-1 text-xs text-red-400 hover:text-red-300">
                  You can upload a maximum of {MAX_IMAGES} images.
                </p>
                  {uploadProgress && !uploadProgress.includes('thumbnail') && (
                    <p className="mt-2 text-sm text-gray-400">{uploadProgress}</p>
                  )}
                  {formData.mediaFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {formData.mediaFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-700 rounded">
                          <div className="flex items-center space-x-2">
                            {file.type.startsWith('image/') ? (
                              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                            <p className="text-sm text-white">
                              {file.name} ({formatFileSize(file.size)})
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeMediaFile(index)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    uploading
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {uploading ? 'Creating...' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && editingEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-2xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Edit Event</h2>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingEvent(null)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="edit-title" className="block text-white font-semibold mb-2">
                    Event Title *
                  </label>
                  <input
                    type="text"
                    id="edit-title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="edit-subtitle" className="block text-white font-semibold mb-2">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    id="edit-subtitle"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  />
                </div>

                <div>
                  <label htmlFor="edit-start_date" className="block text-white font-semibold mb-2">
                    Event Start Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    id="edit-start_date"
                    value={formData.start_date}
                    onChange={(e) => {
                      const selectedDate = e.target.value
                      // Auto-update display date with both start and end dates
                      const displayDate = formData.end_date 
                        ? formatDisplayDateRange(selectedDate, formData.end_date)
                        : formatDateWithTime(selectedDate)
                      setFormData({ 
                        ...formData, 
                        start_date: selectedDate,
                        date: displayDate
                      })
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="edit-end_date" className="block text-white font-semibold mb-2">
                    Event End Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    id="edit-end_date"
                    value={formData.end_date}
                    onChange={(e) => {
                      const selectedEndDate = e.target.value
                      // Auto-update display date with both start and end dates
                      const displayDate = formData.start_date 
                        ? formatDisplayDateRange(formData.start_date, selectedEndDate)
                        : formatDateWithTime(selectedEndDate)
                      setFormData({ 
                        ...formData, 
                        end_date: selectedEndDate,
                        date: displayDate
                      })
                      // Auto-calculate and show status preview
                      const calculatedStatus = calculateStatus(selectedEndDate)
                      if (calculatedStatus === 'past') {
                        setError('')
                        setSuccess('Status will be set to: Past (end date is in the past)')
                      } else {
                        setError('')
                        setSuccess('Status will be set to: Upcoming (end date is in the future)')
                      }
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  />
                  {formData.end_date && (
                    <p className="mt-2 text-sm text-primary-light">
                      Status: {calculateStatus(formData.end_date) === 'upcoming' ? 'Upcoming' : 'Past'} (auto-calculated)
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="edit-date" className="block text-white font-semibold mb-2">
                    Display Date * (Auto-filled: Start Date to End Date with times)
                  </label>
                  <input
                    type="text"
                    id="edit-date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    placeholder="e.g., 22 March 2026 12:24pm to 22 March 2026 3:30pm"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Format: "DD MMMM YYYY HH:MMam/pm to DD MMMM YYYY HH:MMam/pm". Auto-filled from Start & End dates, but you can edit manually.
                  </p>
                  {formData.date && (
                    <p className="mt-2 text-sm text-gray-300">
                      <span className="font-semibold">Preview (Start Date Only):</span> {extractStartDateForDisplay(formData.date)}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="edit-googledrivelink" className="block text-white font-semibold mb-2">
                    Google Drive Link
                  </label>
                  <input
                    type="text"
                    id="edit-googledrivelink"
                    value={formData.googledrivelink}
                    onChange={(e) => setFormData({ ...formData, googledrivelink: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="edit-location" className="block text-white font-semibold mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    id="edit-location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="edit-description" className="block text-white font-semibold mb-2">
                    Description *
                  </label>
                  <textarea
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light resize-none"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="edit-is_active" className="block text-white font-semibold mb-2">
                    Active Status *
                  </label>
                  <select
                    id="edit-is_active"
                    value={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                    required
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="edit-ticket_url" className="block text-white font-semibold mb-2">
                    Ticket URL
                  </label>
                  <input
                    type="url"
                    id="edit-ticket_url"
                    value={formData.ticket_url}
                    onChange={(e) => setFormData({ ...formData, ticket_url: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="edit-thumbnail" className="block text-white font-semibold mb-2">
                    Update Thumbnail (Optional, Max 50MB)
                  </label>
                  <input
                    type="file"
                    id="edit-thumbnail"
                    accept="image/*"
                    onChange={handleThumbnailChange}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  />
                  {uploadProgress && uploadProgress.includes('thumbnail') && (
                    <p className="mt-2 text-sm text-gray-400">{uploadProgress}</p>
                  )}
                  {formData.thumbnail && (
                    <div className="mt-2 flex items-center justify-between p-2 bg-gray-700 rounded">
                      <p className="text-sm text-green-400">
                        New: {formData.thumbnail.name} ({formatFileSize(formData.thumbnail.size)})
                      </p>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, thumbnail: null })}
                        className="text-red-400 hover:text-red-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {!formData.thumbnail && (getThumbnailUrl(editingEvent) || getImageUrl(editingEvent)) && (
                    <p className="mt-2 text-sm text-gray-400">
                      Current thumbnail will be kept if no new file is selected
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="edit-mediaFiles" className="block text-white font-semibold mb-2">
                    Add More Media Files (Images & Videos) - Multiple files allowed (Max 50MB each)
                  </label>
                  <input
                    type="file"
                    id="edit-mediaFiles"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleMediaFilesChange}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    You can select multiple images and videos. These will be added to existing media.
                  </p>
                  {uploadProgress && !uploadProgress.includes('thumbnail') && (
                    <p className="mt-2 text-sm text-gray-400">{uploadProgress}</p>
                  )}
                  {formData.mediaFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm text-primary-light font-semibold">New files to add:</p>
                      {formData.mediaFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-700 rounded">
                          <div className="flex items-center space-x-2">
                            {file.type.startsWith('image/') ? (
                              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                            <p className="text-sm text-white">
                              {file.name} ({formatFileSize(file.size)})
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeMediaFile(index)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingEvent(null)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    uploading
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {uploading ? 'Updating...' : 'Update Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Event Modal */}
      {showViewModal && viewingEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-3xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Event Details</h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setViewingEvent(null)
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Thumbnail */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Thumbnail</label>
                <div className="relative h-64 bg-gray-700 rounded-lg overflow-hidden">
                  {getThumbnailUrl(viewingEvent) || getImageUrl(viewingEvent) ? (
                    <img src={getThumbnailUrl(viewingEvent) || getImageUrl(viewingEvent) || ''} alt={viewingEvent.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-16 h-16 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* Media Files */}
              {getMediaUrls(viewingEvent).length > 0 && (
                <div>
                  <label className="block text-gray-400 text-sm mb-3 font-semibold">
                    Media Files ({getMediaUrls(viewingEvent).length})
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {getMediaUrls(viewingEvent).map((media, index) => (
                      <div key={index} className="relative bg-gray-700 rounded-lg overflow-hidden group hover:shadow-lg transition-shadow">
                        {media.type === 'image' ? (
                          <div className="relative">
                            <img 
                              src={media.url} 
                              alt={`Media ${index + 1}`} 
                              className="w-full h-48 object-cover cursor-pointer"
                              onClick={() => window.open(media.url, '_blank')}
                              onError={(e) => {
                                // Fallback if image fails to load
                                const target = e.target as HTMLImageElement
                                target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23374151" width="400" height="300"/%3E%3Ctext fill="%239CA3AF" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage not found%3C/text%3E%3C/svg%3E'
                              }}
                            />
                            <div className="absolute top-2 right-2">
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-500/90 text-white backdrop-blur-sm">
                                Image
                              </span>
                            </div>
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                              </svg>
                            </div>
                          </div>
                        ) : (
                          <div className="relative">
                            <video 
                              src={media.url} 
                              className="w-full h-48 object-cover" 
                              controls
                              onError={(e) => {
                                // Fallback if video fails to load
                                const target = e.target as HTMLVideoElement
                                target.style.display = 'none'
                                const parent = target.parentElement
                                if (parent) {
                                  parent.innerHTML = `
                                    <div class="w-full h-48 flex items-center justify-center bg-gray-600">
                                      <p class="text-gray-400">Video not available</p>
                                    </div>
                                  `
                                }
                              }}
                            >
                              Your browser does not support the video tag.
                            </video>
                            <div className="absolute top-2 right-2">
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-500/90 text-white backdrop-blur-sm">
                                Video
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Event Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Event Title</label>
                  <p className="text-white font-semibold text-lg">{viewingEvent.title}</p>
                </div>
                {viewingEvent.subtitle && (
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Subtitle</label>
                    <p className="text-primary-light font-semibold">{viewingEvent.subtitle}</p>
                  </div>
                )}
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Date</label>
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-white font-medium">{extractStartDateForDisplay(viewingEvent.date)}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Google Drive Link</label>
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-white font-medium">{viewingEvent.googledrivelink}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Location</label>
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-white font-medium">{viewingEvent.location}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    viewingEvent.status === 'upcoming'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-600 text-gray-300'
                  }`}>
                    {viewingEvent.status === 'upcoming' ? 'Upcoming' : 'Past'}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-400 text-sm mb-1">Description</label>
                  <p className="text-white leading-relaxed">{viewingEvent.description}</p>
                </div>
                {viewingEvent.ticket_url && (
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Ticket URL</label>
                    <a
                      href={viewingEvent.ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-light hover:text-primary-dark underline"
                    >
                      {viewingEvent.ticket_url}
                    </a>
                  </div>
                )}
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Active Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    (viewingEvent.is_active ?? 1) === 1
                      ? 'bg-blue-500 text-white'
                      : 'bg-red-500 text-white'
                  }`}>
                    {(viewingEvent.is_active ?? 1) === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="flex space-x-4 pt-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    openEditModal(viewingEvent)
                  }}
                  className="flex-1 px-6 py-3 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors"
                >
                  Edit Event
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setViewingEvent(null)
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EventManagement

